import type { CoreMessage } from "ai"
import type { DisplayMessage, DisplayToolRun } from "./types"

const MAX_TURNS = 24
const MAX_ARGS = 300
const MAX_RESULT = 500

function clip(s: string, max: number) {
  return s.length > max ? s.slice(0, max) + "…" : s
}

function describeRun(t: DisplayToolRun): string {
  let args = ""
  let result = ""
  try { args = clip(JSON.stringify(t.args ?? {}), MAX_ARGS) } catch { /* unserialisable */ }
  try { result = clip(typeof t.result === "string" ? t.result : JSON.stringify(t.result ?? null), MAX_RESULT) } catch { /* unserialisable */ }
  return `- ${t.toolName}(${args}) → ${t.status === "error" ? "error " : ""}${result}`
}

// Earlier turns go back to the model as text, with a line per tool that ran, so it remembers what
// it did and found without replaying provider-specific tool-call messages
export function toModelHistory(messages: DisplayMessage[]): CoreMessage[] {
  return messages
    .filter(m => !m.isStreaming && (m.content.trim() || m.tools?.length))
    .slice(-MAX_TURNS)
    .map(m => {
      if (m.role === "user") {
        const where = m.route ? `[on ${m.route}] ` : ""
        // Earlier screenshots aren't resent; the model is told they were there
        const shots = m.images?.length ? `\n[${m.images.length} screenshot${m.images.length === 1 ? "" : "s"} attached earlier]` : ""
        return { role: "user" as const, content: where + m.content + shots }
      }
      const runs = (m.tools ?? []).filter(t => t.status === "complete" || t.status === "error")
      const actions = runs.length ? `\n\n[Tools used in this reply]\n${runs.map(describeRun).join("\n")}` : ""
      return { role: "assistant" as const, content: (m.content || "(no text)") + actions }
    })
}
